import { useNotification } from "../components/notification"
import { TYPE_MAP } from "../utils/typesMap"
import { getUser } from "../service/ticket"

type InputPayload = {
    headers: string[]
    rows: any[][]
    commentType?: "public" | "internal";
}

function normalizeValue(value: any) {
    return String(value)
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function normalizeCheckboxValue(value: any): any {
    if (typeof value !== "string") return value;

    const normalized = value.trim().toLowerCase();
    if (normalized === "sim") return true;
    if (normalized === "não" || normalized === "nao") return false;

    return value;
}

function processCellValue(value: any, fieldName?: string): any {
    
    if (typeof value === "number" && value >= 1 && value <= 100000) {
        const converted = excelDateToString(value, fieldName);
        if (!converted.includes("NaN")) {
            return converted;
        }
    }
    
    if (typeof value === "string") {
        const dateStr = value.trim();
        const dateMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
        
        
        if (dateMatch) {
            const acceptsBrazilianFormat = fieldName && (
                fieldName.includes('dd/mm/aaaa') || 
                fieldName.includes('dd/mm/aaaa hh:mm') ||
                fieldName.includes('(dd/mm/aaaa)')
            );
            
            if (acceptsBrazilianFormat) {
                return dateStr;
            }
            
            const [, day, month, year, hours, minutes] = dateMatch;
            if (!hours || hours === "00") {
                return `${year}-${month}-${day}`;
            }
            return `${year}-${month}-${day}T${hours}:${minutes}:00`;
        }
    }
    
    return normalizeCheckboxValue(value);
}

function excelDateToString(value: any, fieldName?: string): any {
    if (typeof value !== "number" || value < 1 || value > 100000) return value;

    const excelEpoch = new Date(1899, 11, 30); 
    
    const days = Math.floor(value);
    const fractionalDay = value - days;
    
    const totalMilliseconds = (days * 24 * 60 * 60 * 1000) + (fractionalDay * 24 * 60 * 60 * 1000);
    const date = new Date(excelEpoch.getTime() + totalMilliseconds);
    
    if (isNaN(date.getTime())) return value;
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    
    const acceptsBrazilianFormat = fieldName && (
        fieldName.includes('dd/mm/aaaa') || 
        fieldName.includes('dd/mm/aaaa hh:mm') ||
        fieldName.includes('(dd/mm/aaaa)')
    );
    
    if (acceptsBrazilianFormat) {
        if (hours === "00") {
            return `${day}/${month}/${year}`;
        }
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    
    if (hours === "00") {
        return `${year}-${month}-${day}`;
    }
    
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
}

const STATUS_MAP: Record<string, string> = {
    "novo": "new",
    "aberto": "open",
    "pendente": "pending",
    "em espera": "hold",
    "espera": "hold",
    "resolvido": "solved",
    "fechado": "closed"
};

const PRIORITY_MAP: Record<string, string> = {
    "baixa": "low",
    "baixo": "low",
    "low": "low",
    "normal": "normal",
    "alta": "high",
    "high": "high",
    "urgente": "urgent",
    "urgent": "urgent"
};

function normalizeStatusValue(value: any): any {
    if (typeof value !== "string") return value;

    const normalized = value.trim().toLowerCase();
    return STATUS_MAP[normalized] || value;
}

function normalizePriorityValue(value: any): any {
    if (typeof value !== "string") return value;

    const normalized = normalizeValue(value);
    return PRIORITY_MAP[normalized] || value;
}


export function useFormatResponses() {
    const { showNotification } = useNotification()

    function buildUsersPayload(data: InputPayload) {
        const { headers, rows } = data

        const nameIndex = headers.indexOf("name")
        const cpfIndexes = headers
            .map((h, i) => h.toLowerCase().includes("cpf") ? i : -1)
            .filter(i => i !== -1)

        if (nameIndex === -1) {
            showNotification({
                type: "warning",
                title: "Modelo de Planilha Inválido",
                message: "Para a criação massiva de usuários, os campos obrigatórios são: 'name' e 'role'. O 'email' é opcional.",
            });
            throw new Error("Os headers obrigatórios 'name' e 'role' não foram encontrados na planilha.");
        }


        return {
            users: rows
                .filter(row => Array.isArray(row) && row.some(v => v !== undefined && v !== null && v !== ""))
                .map(row => {
                    const userFields: Record<string, any> = {}

                    const cnpjIndexes = headers
                        .map((h, i) => h.toLowerCase().includes("cnpj") ? i : -1)
                        .filter(i => i !== -1)

                    headers.forEach((header, index) => {
                        if (header === "name" || header === "email" || header === "role") return

                        let value = row[index]

                        if (cpfIndexes.includes(index) && value) {
                            value = String(value).replace(/[^0-9]/g, "");
                        }

                        if (cnpjIndexes.includes(index) && value) {
                            value = String(value).replace(/[^0-9]/g, "");
                        }

                        if (
                            value === undefined ||
                            value === null ||
                            value === "" ||
                            value === "null"
                        ) {
                            return
                        }

                        userFields[header] =
                            processCellValue(value, header)
                    })

                    const name =
                        processCellValue(
                            typeof row[nameIndex] === "string"
                                ? row[nameIndex].trim()
                                : row[nameIndex]
                        )

                    const emailIndex = headers.indexOf("email")
                    const email = emailIndex !== -1
                        ? processCellValue(
                            typeof row[emailIndex] === "string"
                                ? row[emailIndex].trim()
                                : row[emailIndex]
                        )
                        : undefined

                    const cpfFieldKey = Object.keys(userFields).find(key =>
                        key.startsWith('cpf')
                    )
                    const cnpj = Object.keys(userFields).find(key =>
                        key.startsWith('cnpj')
                    )
                    const external_id =  cnpj ? userFields[cnpj] :
                    cpfFieldKey ? userFields[cpfFieldKey] : ''
                    return {
                        name,
                        email,
                        external_id: external_id,
                        ...(Object.keys(userFields).length > 0 && {
                            user_fields: userFields
                        })
                    }
                })
        }
    }

    function buildOrganizationsPayload(data: InputPayload) {
        const { headers, rows } = data;

        const nameIndex = headers.indexOf("name");

        const notHeader = headers.indexOf("role")

        if (nameIndex === -1) {
            showNotification({
                type: "warning",
                title: "Modelo de Planilha Inválido",
                message: "Para a criação massiva de organizações, o campo obrigatório é: 'name'.",
            });
            throw new Error("O header obrigatório 'name' não foi encontrado na planilha para organizações.");
        }

        if (notHeader === 2) {
            showNotification({
                type: "warning",
                title: "Modelo de Planilha Inválido",
                message: "Reveja os dados da planilha inserida",
            });
            throw new Error("O header não pode conter 'role'");
        }


        return {
            organizations: rows
                .filter(row => Array.isArray(row) && row.some(v => v !== "" && v != null))
                .map(row => {
                    const orgFields: Record<string, any> = {};

                    headers.forEach((header, i) => {
                        if (header === "name") return;

                        const value = row[i];
                        if (value == null || value === "") return;

                        orgFields[header] = processCellValue(value, header);
                    });

                    return {
                        name: row[nameIndex]?.trim(),
                        ...(Object.keys(orgFields).length > 0 && {
                            organization_fields: orgFields,
                        }),
                    };
                })
                .filter(org => Boolean(org.name))
        };
    }

    async function buildTicketsPayload({ headers, rows, currentUserId, commentType = "public" }: InputPayload & { currentUserId: number }) {
        const required = ["subject", "comment", "type"];
        const VALID_TYPES = Object.keys(TYPE_MAP);

        const missing = required.filter(r => !headers.includes(r));
        if (missing.length > 0) {
            showNotification({
                type: "warning",
                title: "Modelo de Ticket Inválido",
                message: "Campos obrigatórios faltando: " + missing.join(", "),
            });
            throw new Error("Required fields missing");
        }

        const priorityIndex = headers.indexOf("priority");
        if (priorityIndex !== -1) {
            const invalid = rows.filter(r => {
                if (!r[priorityIndex]) return false;
                const normalized = normalizeValue(r[priorityIndex]);
                return !Object.keys(PRIORITY_MAP).includes(normalized);
            });
            if (invalid.length) {
                showNotification({
                    type: "warning",
                    title: "Prioridade inválida",
                    message: "Use: baixa, normal, alta ou urgente (ou em inglês: low, normal, high, urgent)",
                });
                throw new Error("Invalid priority");
            }
        }

        const typeIndex = headers.indexOf("type");
        if (typeIndex !== -1) {
            const invalid = rows.filter(r => {
                if (!r[typeIndex]) return false;

                const normalized = normalizeValue(r[typeIndex]);
                return !VALID_TYPES.includes(normalized);
            });

            if (invalid.length) {
                showNotification({
                    type: "warning",
                    title: "Tipo inválido",
                    message: "Use: ticket, incidente, problema, pergunta ou tarefa",
                });
                throw new Error("Invalid type");
            }
        }

        const requesterEmailIndex = headers.indexOf("requester_email");
        const emailToIdMap: Record<string, number> = {};

        if (requesterEmailIndex !== -1) {
            const uniqueEmails = [...new Set(rows
                .map(row => row[requesterEmailIndex])
                .filter(email => email && typeof email === "string" && email.trim() !== ""))];

            if (uniqueEmails.length > 0) {
                try {
                    const tags = uniqueEmails.map(email => ({
                        searchOption: "Email",
                        value: (email as string).trim(),
                        ID: "temp"
                    }));
                    const result = await getUser(tags);
                    if (result.users && Array.isArray(result.users)) {
                        result.users.forEach((user: any) => {
                            if (user.email) {
                                emailToIdMap[user.email.toLowerCase()] = user.id;
                            }
                        });
                    }
                } catch (error) {
                    console.error("Erro ao buscar usuários:", error);
                }
            }
        }

        return {
            tickets: rows
                .filter(row => row.some(v => v !== undefined && v !== ""))
                .map(row => {
                    const ticket: any = {};
                    const custom_fields: any[] = [];

                    const hasRequesterCpf = headers.includes("requester_cpf") && row[headers.indexOf("requester_cpf")];
                    const hasRequesterEmail = requesterEmailIndex !== -1 && row[requesterEmailIndex];

                    if (!hasRequesterCpf && !hasRequesterEmail) {
                        ticket.requester_id = currentUserId;
                    }

                    headers.forEach((header, index) => {
                        const value = row[index];
                        if (value === undefined || value === "" || value === null) return;

                        if (header === "ticket_form") {
                            ticket.ticket_form_id = value;
                            return;
                        }

                        if (header === "status") {
                            ticket.status = normalizeStatusValue(value);
                            return;
                        }

                        if (header === "type") {
                            const normalized = normalizeValue(value);
                            ticket.type = TYPE_MAP[normalized];
                            return;
                        }

                        if (header === "priority") {
                            ticket.priority = normalizePriorityValue(value);
                            return;
                        }


                        if (header.startsWith("[CF:")) {
                            const match = header.match(/\[CF:\s*(\d+)\]\s*(.*)/);
                            const id = match ? Number(match[1]) : null;
                            const fieldName = match ? match[2].trim() : null;

                            if (id) {
                                const normalizedValue = processCellValue(value, fieldName || id.toString());
                                custom_fields.push({ id: fieldName || id, value: normalizedValue });
                            }
                            return;
                        }

                        const baseFields = [
                            "ticket_form", "subject", "comment", "type", "priority", "status",
                            "requester_email", "requester_cpf", "group", "brand",
                            "assignee_email", "description", "subject", "due_at", "tags",
                            "custom_status_id", "custom_fields", "organization_id", "assignee_id",
                            "requester_id", "external_id", "via_id", "followup_id", "parent_id"
                        ];

                        if (!baseFields.includes(header.toLowerCase()) &&
                            !header.startsWith("additional_")) {
                            const isCpfCnpjField = header.toLowerCase().includes('cpf') || header.toLowerCase().includes('cnpj');
                            let normalizedValue = value;
                            if (isCpfCnpjField && value) {
                                normalizedValue = String(value).replace(/[^0-9]/g, "");
                            } else {
                                normalizedValue = processCellValue(value, header);
                            }
                            custom_fields.push({ id: header, value: normalizedValue });
                            return;
                        }

                        if (header === "comment") {
                            ticket.comment = { body: value, author_id: currentUserId, public: commentType === "public" };
                            return;
                        }

                        if (header === "description") {
                            ticket.description = value;
                            return;
                        }

                        if (header === "requester_email") {
                            const email = (value as string).trim().toLowerCase();
                            const userId = emailToIdMap[email];
                            if (userId) {
                                ticket.requester_id = userId;
                            } else {
                                ticket.requester_email = value;
                                showNotification({
                                    type: "warning",
                                    title: "Usuário não encontrado",
                                    message: `O email ${email} não foi encontrado no Zendesk.`,
                                });
                            }
                            return;
                        }

                        if (header === "requester_cpf") {
                            ticket.requester_cpf = String(value);
                            return;
                        }

                        ticket[header] = processCellValue(value, header);
                    });

                    if (custom_fields.length) {
                        ticket.custom_fields = custom_fields;
                    }
                    return ticket;
                }),
        };
    }



    return {
        buildUsersPayload,
        buildOrganizationsPayload,
        buildTicketsPayload
    }
}

